/** Shared class builders for buttons and form controls (kept out of component files for fast refresh). */
import { cn } from "@/lib/cn";

export type ButtonVariant = "primary" | "ghost" | "danger" | "subtle";
export type ButtonSize = "sm" | "md" | "lg" | "icon";

/** Shared class builder so links and buttons look identical. */
export function buttonStyles({
  variant = "primary",
  size = "md",
  fullWidth = false,
  className,
}: { variant?: ButtonVariant; size?: ButtonSize; fullWidth?: boolean; className?: string } = {}) {
  return cn(
    "relative inline-flex select-none items-center justify-center gap-2 whitespace-nowrap rounded-full font-medium",
    "transition-[background-color,box-shadow,color,transform] duration-200 ease-out active:scale-[.98]",
    "disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50",
    {
      sm: "h-9 px-4 text-sm",
      md: "h-11 px-5 text-[15px]",
      lg: "h-[52px] px-7 text-base",
      icon: "size-11 p-0",
    }[size],
    {
      primary: "edge-light bg-[var(--primary-strong)] text-white shadow-glow-1 hover:bg-primary-hover hover:shadow-glow-2",
      ghost: "border border-line bg-fg/[0.03] text-ink hover:border-[var(--border-glow)] hover:bg-fg/[0.06] hover:shadow-glow-1",
      danger: "bg-[var(--danger-strong)] text-white hover:shadow-glow-danger",
      subtle: "text-ink-soft hover:bg-fg/[0.06] hover:text-ink",
    }[variant],
    fullWidth && "w-full",
    className,
  );
}

export const controlStyles = cn(
  "w-full rounded-input border border-line bg-elevated/70 px-3.5 text-[15px] text-ink placeholder:text-ink-muted/80",
  "transition-[border-color,box-shadow] duration-200",
  "hover:border-[#2f3854] focus:border-primary focus:shadow-glow-1 focus:outline-none focus-visible:shadow-glow-1",
  "aria-[invalid=true]:border-danger aria-[invalid=true]:shadow-[0_0_0_1px_rgb(var(--rgb-danger)/0.4)]",
  "disabled:cursor-not-allowed disabled:opacity-60",
);
